/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from "react"
import { OrganizationData, SYMPHONY_TASKS } from "modules/Types"
import { Body2, Subtitle1 } from "components/shareable/Typography"
import useStyles from "./styles"
import { UpgradeModalContext } from "Hooks/UpgradeModalContext"
import { DataGrid } from "@mui/x-data-grid"
import { Row, getDataGridColumns } from "./utils"
import AddUserModal from "../AddUserModal"
import UpgradeToProChip from "components/shareable/UpgradeToProChip"
import { useHistory } from "react-router-dom"
import { OrganizationTab } from "../../../OrganizationPage/Organization_constants"
import { BrandUsersApiResponse } from "api/brandApi"
import useCurrentBrand from 'Hooks/useCurrentBrand'
import Sty, { Container, StyledLabelP } from "modules/Sty"
import { PlanType, PRO_PLAN_ADDITIONAL_SEAT_PRICE } from "modules/Const"
import ButtonSymphony from "elements/ButtonSymphony"
import Colors from "modules/Colors"
import ModalSymphony from "elements/ModalSymphony"
import Banner from "elements/Banner"
import Loader from "elements/Loader"
import { toastError, toastSuccess } from "modules/Toast"
import useCurrentUser from "Hooks/useCurrentUser"

interface UsersTabProps {
    brandBelongsToOrganization?: boolean;
    organization?: OrganizationData;
}

const UsersTab = ({
    brandBelongsToOrganization,
}: UsersTabProps) => {
    const history = useHistory();
    const classes = useStyles()
    const { currentUser } = useCurrentUser()
    const {
        isProBrand,
        deleteUserFromBrand,
        getBrandUsers,
    } = useCurrentBrand()

    const { handleOpenModal: openUpgradeToProModal } = useContext(UpgradeModalContext)

    const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false)
    const [showDeleteUserModal, setShowDeleteUserModal] = useState<boolean>(false)
    const [isLoadingBrandUsers, setIsLoadingBrandUsers] = useState<boolean>(false)

    const [pulledUsers, setPulledUsers] = useState<BrandUsersApiResponse>({ availableSeats: 0, users: [] })
    const [isHandlingEditAction, setIsHandlingEditAction] = useState<boolean>(false)

    // user to update
    const [userToEdit, setUserToEdit] = useState<Row | null>(null)

    const dataGridColumns = getDataGridColumns({
        onRemove: (row: Row | null) => {
            if (brandBelongsToOrganization) {
                history.push(`/team?tab=${OrganizationTab.TEAM}`)
            } else {
                setUserToEdit(row as Row | null)
                setShowDeleteUserModal(true)
            }
        },
    })

    const fetchUsers = async () => {
        setIsLoadingBrandUsers(true)
        try {
            const { users, availableSeats } = await getBrandUsers()
            setPulledUsers({ users, availableSeats })
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoadingBrandUsers(false)
        }
    }

    useEffect(() => {
        fetchUsers()
    }, [])

    const handleCloseAddModal = () => {
        setShowAddUserModal(false)
    }

    const handleCloseDeleteModal = async () => {
        setUserToEdit(null)
        await fetchUsers()
        setShowDeleteUserModal(false)
    }

    const handleClickAddNewMemberButton = () => {
        if (brandBelongsToOrganization) {
            history.push(`/team?tab=${OrganizationTab.TEAM}`)
        } else {
            if (isProBrand) {
                setShowAddUserModal(true)
            } else {
                openUpgradeToProModal({
                    source: "Settings - Add New User",
                    highlightedPlan: PlanType.PRO,
                    upgradeCallToAction: "Invite your team with Symphony Pro"
                })
            }
        }
    }

    const handleDeleteUser = async () => {
        setIsHandlingEditAction(true)
        try {
            const { users, availableSeats } = await deleteUserFromBrand({
                id: userToEdit?.id!,
                email: userToEdit?.email!
            })
            setPulledUsers({ users, availableSeats })
        } catch (error) {
            console.error(error)
            toastError(`There was an error deleting ${userToEdit?.email} from your profile - please try again.`)
        } finally {
            toastSuccess(`${userToEdit?.email} has been removed from your profile.`)
            setShowDeleteUserModal(false)
            setIsHandlingEditAction(false)
        }
    }

    const renderSubscriptionChangeNotice = () => {
        if (pulledUsers.users?.length <= 2) {
            return null
        }

        return (
            <Banner
                color="purple"
                className="deleteUserModalBanner"
            >
                <Subtitle1>Your monthly subscription will be reduced.</Subtitle1>
                <Body2 color={Colors.textColor}>
                    Since you're removing an extra user, your next invoice will be
                    reduced by ${PRO_PLAN_ADDITIONAL_SEAT_PRICE}.
                </Body2>
            </Banner>
        )
    }

    const renderDeleteUserModal = () => {
        return (
            <ModalSymphony
                className="deleteUserModalSettingsPage"
                isOpen={showDeleteUserModal}
                onClose={handleCloseDeleteModal}
                title="Remove member"
                subtitle="Are you sure you want to remove this member?"
                buttonLeftText="Cancel"
                buttonRightText="Remove member"
                buttonRightColor="red"
                onClickButtonLeft={handleCloseDeleteModal}
                onClickButtonRight={handleDeleteUser}
                buttonRightIsLoading={isHandlingEditAction}
                buttonRightDisabled={isHandlingEditAction}
                buttonLeftDisabled={isHandlingEditAction}
            >
                <Subtitle1>{userToEdit?.email}</Subtitle1>
                {renderSubscriptionChangeNotice()}
            </ModalSymphony>
        )
    }

    const renderAddUserModal = () => {
        return (
            <AddUserModal
                handleUpdateUsers={(users) => {
                    setPulledUsers({ users, availableSeats: pulledUsers.availableSeats })
                }}
                open={showAddUserModal}
                onClose={handleCloseAddModal}
                currentUserCount={pulledUsers.users?.length}
            />
        )
    }

    const renderUsersList = () => {
        if (isLoadingBrandUsers || !pulledUsers.users) {
            return (
                <Loader className="usersListLoader" title="Loading Users..." style={{ marginTop: 24 }} />
            )
        }

        const rows = pulledUsers.users.map(({
            email,
            id,

        }) => {
            return {
                id,
                email,
            }
        })

        return (
            <Container margin="0px" minHeight="100px">
                <DataGrid
                    autoHeight
                    headerHeight={0}
                    hideFooter
                    classes={{ root: classes.dataGridRoot }}
                    columns={dataGridColumns}
                    //TODO: Data will change from the same endpoint
                    rows={pulledUsers.users.map(({
                        email,
                        id,
                        name,
                        isOwner
                    }) => {
                        let isRemovable = currentUser?.email !== email
                        return {
                            id,
                            email,
                            isOwner: isOwner,
                            name: name || "-",
                            removable: isRemovable,
                        }
                    }
                    )}
                />
            </Container>
        )
    }

    return (
        <>
            <Container className="usersTabComponent">
                <StyledLabelP>
                    <Container
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        padding="0px 0px 24px 0px"
                        borderBottom="1px solid #E5E7EB"
                    >
                        <div className="settingsPageProfileMembersTitle" style={Sty.text20}>
                            Profile Members
                        </div>
                        <ButtonSymphony
                            className="settingsPageInviteMemberButton"
                            onClick={handleClickAddNewMemberButton}
                            variant="text"
                            iconLeft={<UpgradeToProChip type="gem" color="purple" height={18} width={18} />}
                        >
                            Invite a Member
                        </ButtonSymphony>
                    </Container>
                </StyledLabelP>
                {renderUsersList()}
            </Container>
            {/* MODALS */}
            {renderDeleteUserModal()}
            {renderAddUserModal()}

            {/* <AddUserModal
                {...{ brandId }}
                users={pulledUsers.users}
                hasReachedLimit={hasReachedLimit}
                open={addUserModal.open}
                onClose={handleCloseAddModal}
                onSaveUsers={setPulledUsers}
            /> */}
        </>
    )
}

export default UsersTab
